import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Ticket, CreditCard, Crown, ShieldCheck, Wallet as WalletIcon, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PayPalCheckoutButton from "@/components/PayPalCheckoutButton";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";
import PaystackButton from "@/components/PaystackButton";
import { PAYMENT_LINKS, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";

const services = [
  { id: "starter_monthly", sub: true, name: "Starter", price: "£4.99", priceNum: 4.99, suffix: "/month", stripeId: "starter_month", desc: "For users beginning to execute seriously.", features: ["Goal planning + 30-day roadmap", "Daily Briefing + daily actions", "Basic AI assistants + CV Builder", "Basic Truffl opportunity discovery", "Marketplace + affiliate access", "Progress tracking"] },
  { id: "pro_monthly", sub: true, name: "Pro", price: "£14.99", priceNum: 14.99, suffix: "/month", stripeId: "premium_month", annualTier: "pro_annual", annualPrice: "£149/year", desc: "The complete Zuntria execution system for individuals.", features: ["Everything in Starter", "Advanced Daily Briefing + Next Best Action", "Full Truffl client engine + Client Finder", "Get Paid This Week plans", "Ajo savings circles + Money Health Score", "Career, business & freelance execution", "All AI capabilities + AI coaching", "Affiliate earnings + partner opportunities"], highlight: true, badge: "MOST POPULAR" },
  { id: "business_monthly", sub: true, name: "Business", price: "£49", priceNum: 49, suffix: "/month", stripeId: "business_month", annualTier: "business_annual", annualPrice: "£490/year", desc: "For freelancers, tradespeople, consultants and small businesses.", features: ["Everything in Pro", "Business automation + CRM workflows", "Advanced opportunity engine", "Zapier / Make outbound webhooks (HMAC-signed)", "Team functionality + business analytics", "Priority AI & support", "Ajo circles up to 50"] },
  { id: "enterprise", enterprise: true, name: "Enterprise", price: "From £499", priceNum: 499, suffix: "/month", desc: "For larger organisations — custom contracts, integrations and support.", features: ["Organisation management", "Advanced API + custom integrations", "Enterprise analytics + custom workflows", "High-volume opportunity processing", "Advanced permissions + SSO options", "Enterprise support + custom commercials"] },
  { id: "hustle_plan", name: "Hustle Plan", price: "£5", priceNum: 5, desc: "7-day personalised income plan." , features: ["7-day plan", "Tailored to your skills", "Action checklist"] },
  { id: "ajo_plan", name: "Ajo Plan", price: "£10", priceNum: 10, desc: "Full Ajo schedule, rules & WhatsApp message.", features: ["Custom schedule", "Group rules", "WhatsApp-ready message"] },
  { id: "business_plan", name: "Business Plan", price: "£15", priceNum: 15, desc: "Full business plan + branding + marketing.", features: ["Business plan", "Brand direction", "Marketing playbook"] },
  { id: "cv", name: "CV", price: "£7", priceNum: 7, desc: "Professional ATS-friendly CV.", features: ["ATS-friendly CV", "Tailored to role", "Instant PDF"] },
  { id: "cover_letter", name: "Cover Letter", price: "£7", priceNum: 7, desc: "Tailored cover letter.", features: ["Personalised", "Tone control", "Instant PDF"] },
  { id: "logo", name: "Logo & Branding", price: "£20", priceNum: 20, desc: "Logo concepts, palette, slogan, identity.", features: ["3 logo concepts", "Colour palette", "Brand voice"] },
];

const CREDIT_PRICES = { pro_monthly: 200, premium_month: 500 };

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const locale = useLocale();
  const [selected, setSelected] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState("choose"); // choose | confirm
  const [method, setMethod] = useState(null);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [payingCredits, setPayingCredits] = useState(false);

  const payWithCredits = async () => {
    if (!selected) return;
    setPayingCredits(true);
    try {
      const { data } = await api.post("/rewards/pay-with-credits", { product_id: selected.id });
      toast.success(`${data.label} unlocked with credits! ${data.credits_left} ⚡ left`);
      setSelected(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not pay with credits");
    } finally { setPayingCredits(false); }
  };

  // Stripe return polling
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) { toast.info("Payment cancelled"); setParams({}, { replace: true }); return; }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid" && data.credited) {
          setParams({}, { replace: true });
          if (data.package_id === "premium_month") {
            toast.success("Premium unlocked", { description: "Unlimited AI + 2× rewards active for 30 days." });
          } else {
            toast.success(`${data.label || "Service"} purchased`);
          }
          refresh();
          return;
        }
      } catch (err) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 10) { setParams({}, { replace: true }); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = (svc) => {
    setSelected(svc); setStep("choose"); setMethod(null);
    setReference(""); setNote("");
  };

  const payWithCard = async (svc) => {
    if (!user) { toast.error("Please log in to pay by card"); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: svc.stripeId || svc.id,
        origin_url: window.location.origin + "/pricing",
        kind: "service",
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    }
  };

  const submitOrder = async () => {
    if (!selected || !method) return;
    if (!user) { toast.error("Please log in first"); return; }
    setSubmitting(true);
    try {
      await api.post("/orders", {
        product_id: selected.id,
        method,
        payment_reference: reference,
        note,
      });
      toast.success("Order submitted", {
        description: "We'll confirm within 24h. You'll get a notification when ready.",
        duration: 10000,
      });
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit order");
    } finally { setSubmitting(false); }
  };

  const redeemCoupon = async () => {
    if (!coupon.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/coupons/redeem", { code: coupon });
      toast.success(`Coupon applied: ${data.applied.label}`);
      setCoupon("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
    } finally { setRedeeming(false); }
  };

  const premiumActive = user?.premium_until && new Date(user.premium_until) > new Date();
  const isLifetime = user?.subscription_plan === "lifetime_pro" || user?.founding_member;
  const [cancelling, setCancelling] = useState(false);

  const cancelSubscription = async () => {
    if (!window.confirm(t("pricing.cancel_confirm", "Cancel your subscription? You keep access until the end of your paid period."))) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/paypal/cancel-subscription");
      toast.success(t("pricing.cancelled", "Subscription cancelled"), {
        description: data.access_until ? `${t("pricing.access_until", "Access until")} ${new Date(data.access_until).toLocaleDateString()}` : undefined,
      });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel — try again");
    } finally { setCancelling(false); }
  };

  // After user picks a method, show the "I've paid" form (except Stripe which redirects)
  const goToConfirmStep = (m) => {
    if (m === "stripe") { selected && payWithCard(selected); setSelected(null); return; }
    setMethod(m);
    setStep("confirm");
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("pricing.kicker")}</p>
        <h1 className="mt-2 font-display text-4xl font-medium tracking-tighter sm:text-5xl">{t("pricing.title")}</h1>
        <p className="mt-4 text-muted-foreground">{t("pricing.subtitle")}</p>
        {locale?.paymentNote && locale?.paymentProvider !== "paypal_stripe" && (
          <p className="mx-auto mt-2 max-w-md rounded-full border border-border bg-card px-4 py-1.5 text-xs text-muted-foreground" data-testid="regional-payment-note">
            {locale.paymentNote}
          </p>
        )}
        {isLifetime && (
          <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-sm text-amber-400" data-testid="lifetime-badge">
            <Crown className="h-3.5 w-3.5" /> {t("pricing.lifetime_badge", "Founding Lifetime Pro — you never pay again")}
          </div>
        )}
        {!isLifetime && premiumActive && (
          <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-sm text-accent" data-testid="premium-badge">
            <Crown className="h-3.5 w-3.5" /> Premium active until {new Date(user.premium_until).toLocaleDateString()}
          </div>
        )}
        {user?.subscription_recurring && (
          <div className="mx-auto mt-3 flex max-w-md items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-2.5" data-testid="manage-subscription-card">
            <span className="text-xs text-muted-foreground">
              {user?.subscription_past_due
                ? t("pricing.past_due", "⚠️ Payment issue — update your PayPal payment method")
                : t("pricing.sub_active", "Auto-renewing subscription active")}
            </span>
            <Button size="sm" variant="outline" onClick={cancelSubscription} disabled={cancelling} data-testid="cancel-subscription-btn">
              {cancelling ? "…" : t("pricing.cancel_sub", "Cancel")}
            </Button>
          </div>
        )}
        <div className="mx-auto mt-6 flex max-w-md items-center gap-2 rounded-full border border-border bg-card px-2 py-1.5" data-testid="coupon-bar">
          <Ticket className="ml-2 h-4 w-4 text-accent" />
          <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Got a coupon? e.g. WELCOME5" className="h-9 border-0 bg-transparent focus-visible:ring-0" data-testid="coupon-input" />
          <Button size="sm" onClick={redeemCoupon} disabled={redeeming || !coupon} data-testid="coupon-apply">Apply</Button>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-muted-foreground"><ShieldCheck className="h-3 w-3 text-emerald-500" /> {t("pricing.secure")}</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-muted-foreground">{t("pricing.no_lock")}</span>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {services.map((s) => {
          return (
          <div
            key={s.id}
            data-testid={`tier-${s.id}`}
            className={`flex flex-col rounded-2xl border bg-card p-6 md:p-8 ${s.highlight ? "border-primary glow-cobalt" : "border-border"}`}
          >
            {s.highlight && (
              <div className="mb-3 inline-flex w-fit items-center rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent" data-testid={`badge-${s.id}`}>
                <Crown className="mr-1 h-3 w-3" /> {s.badge || "Most popular"}
              </div>
            )}
            <h3 className="font-display text-xl">{s.name}</h3>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display text-3xl">{s.price}</span>
              {s.suffix && <span className="text-sm text-muted-foreground">{s.suffix}</span>}
            </div>
            {s.annualPrice && (
              <p className="mt-0.5 text-xs text-emerald-400" data-testid={`annual-price-${s.id}`}>or {s.annualPrice} — 2 months free</p>
            )}
            {locale?.convertFromGBP?.(s.priceNum) != null && (
              <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`local-price-${s.id}`}>
                {t("pricing.approx", { amount: formatMoney(locale.convertFromGBP(s.priceNum), locale.currency) })}
              </p>
            )}
            <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
            <ul className="mt-5 space-y-2 text-sm">
              {s.features.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{f}</span></li>
              ))}
            </ul>
            <Button
              onClick={() => s.enterprise
                ? window.open("mailto:hello@zuntria.co.uk?subject=Zuntria%20Enterprise%20enquiry", "_blank")
                : openDialog(s)}
              className="mt-auto h-12 w-full bg-accent text-base text-accent-foreground hover:bg-accent/90"
              data-testid={`tier-cta-${s.id}`}
            >
              {s.enterprise ? "Contact us" : t("pricing.buy_now")} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          );
        })}
      </div>

      {/* Payment dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="payment-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {step === "choose" ? "How do you want to pay?" : "Confirm your payment"}
            </DialogTitle>
            <DialogDescription>
              {selected ? `${selected.name} · ${selected.price}${selected.suffix || ""}` : ""}
            </DialogDescription>
          </DialogHeader>

          {step === "choose" && selected && (
            <div className="space-y-3">
              {/* ⚡ Pay with Zuntria Credits */}
              {user && CREDIT_PRICES[selected.id] && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" data-testid="pay-credits-card">
                  <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-wider text-amber-500">
                    <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[9px] font-bold text-white">CREDITS</span>
                    <span>You have {Number(user.zuntria_credits || 0)} ⚡</span>
                  </div>
                  <Button
                    onClick={payWithCredits}
                    disabled={payingCredits || Number(user.zuntria_credits || 0) < CREDIT_PRICES[selected.id]}
                    className="w-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                    data-testid="pay-with-credits-btn"
                  >
                    {payingCredits ? "Processing…" : `Pay with ${CREDIT_PRICES[selected.id]} Zuntria Credits`}
                  </Button>
                  {Number(user.zuntria_credits || 0) < CREDIT_PRICES[selected.id] && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">Earn credits by referring friends & completing goals.</p>
                  )}
                </div>
              )}
              {/* 🚀 AUTO — Official PayPal (subscriptions auto-renew, one-off products capture instantly) */}
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="paypal-auto-card">
                <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-primary">
                  <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-bold text-primary-foreground">AUTO</span>
                  <span>
                    {selected.sub
                      ? t("pricing.sub_cta_note", "Auto-renews — cancel anytime in one click")
                      : `Instant delivery — pay & get your ${selected.name} in 60 seconds`}
                  </span>
                </div>
                {selected.sub ? (
                  <>
                    <PayPalSubscribeButton
                      tier_id={selected.id}
                      price={`${selected.price}${selected.suffix || ""}`}
                      onSuccess={() => { setSelected(null); refresh(); }}
                    />
                    {selected.annualTier && (
                      <div className="mt-2 border-t border-border pt-2" data-testid="annual-option">
                        <p className="mb-1 text-[11px] text-muted-foreground">Save with annual — {selected.annualPrice} (2 months free)</p>
                        <PayPalSubscribeButton
                          tier_id={selected.annualTier}
                          price={selected.annualPrice}
                          onSuccess={() => { setSelected(null); refresh(); }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <PayPalCheckoutButton
                    product_id={selected.id}
                    price={selected.price}
                    onSuccess={() => { setSelected(null); refresh(); toast.success("Done!"); }}
                  />
                )}
                <PaystackButton product_id={selected.id} price={`${selected.price}${selected.sub ? selected.suffix || "" : ""}`} />
              </div>

              <div className="relative flex items-center gap-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <span>or pay manually</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              {/* MANUAL — Revolut */}
              <a
                href={PAYMENT_LINKS.revolut}
                target="_blank"
                rel="noreferrer"
                onClick={() => setTimeout(() => goToConfirmStep("revolut"), 800)}
                className="flex w-full items-center justify-between rounded-xl bg-[#0075EB] px-5 py-4 text-base font-semibold text-white shadow-lg shadow-[#0075EB]/30 transition-transform hover:scale-[1.02]"
                data-testid="pay-revolut-btn"
              >
                <span className="flex items-center gap-2">
                  <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[10px] tracking-wider">REVOLUT</span>
                  Pay {selected.price}
                </span>
                <ArrowRight className="h-5 w-5" />
              </a>

              {/* MANUAL — PayPal.me fallback */}
              <a
                href={PAYMENT_LINKS.paypal}
                target="_blank"
                rel="noreferrer"
                onClick={() => setTimeout(() => goToConfirmStep("paypal"), 800)}
                className="flex w-full items-center justify-between rounded-xl bg-[#003087] px-5 py-4 text-base font-semibold text-white shadow-lg shadow-[#003087]/30 transition-transform hover:scale-[1.02]"
                data-testid="pay-paypal-btn"
              >
                <span className="flex items-center gap-2">
                  <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[10px] tracking-wider">PAYPAL.ME</span>
                  Pay {selected.price}
                </span>
                <ArrowRight className="h-5 w-5" />
              </a>

              <div className="mt-3 rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-[11px] text-muted-foreground">
                <strong className="block text-foreground">After you pay (manual flow):</strong>
                Click any of the manual buttons above — once you've sent the money, come back and drop your transaction reference. We'll deliver within 1h.
              </div>
            </div>
          )}

          {step === "confirm" && selected && (
            <div className="space-y-4">
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                Paid via <strong className="capitalize">{method?.replace("_", " ")}</strong>? Drop your reference below and we'll deliver your <strong>{selected.name}</strong> within 24h.
              </div>
              <div className="space-y-1.5">
                <Label>Payment reference / transaction ID</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g. Revolut tx 8K9D2... or 'sent via PayPal'"
                  className="h-11"
                  data-testid="order-reference-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Anything we should know? (optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. role I'm applying for, business idea, design vibe..."
                  className="min-h-[80px]"
                  data-testid="order-note-input"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep("choose")} className="flex-1" data-testid="order-back-btn">Back</Button>
                <Button
                  onClick={submitOrder}
                  disabled={submitting || !reference.trim()}
                  className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="order-submit-btn"
                >
                  {submitting ? "Submitting…" : "I've paid — submit order"}
                </Button>
              </div>
              <p className="text-center text-[11px] text-muted-foreground">
                <WalletIcon className="mr-1 inline h-3 w-3 text-primary" />
                Your order goes straight to our admin. You'll get a notification when confirmed.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
